import { RedirectBoundary as CometRedirectBoundary, useAuth } from '@zg-rentals/auth-comet';
import { ComponentProps, ComponentType } from 'react';
import { ErrorPage } from '@zillow/zrs-zillow-offers';
import pathsManifest from '../utils/pathsManifest';

export function RedirectBoundary(props: ComponentProps<typeof CometRedirectBoundary>) {
  const paths = pathsManifest();
  const isDevelopment = process.env.NODE_ENV === 'development';

  const homePageText = isDevelopment ? 'Return to SLW Dev Tools Page' : 'Return to Zillow Rental Manager';

  const homePageUrl = isDevelopment ? paths.devToolsPage : paths.homePage;

  const onError = (errorMessage: string, errorCode: number) => (
    <ErrorPage
      errorCode={errorCode}
      errorMessage={errorMessage}
      homePageText={homePageText}
      homePageUrl={homePageUrl}
    />
  );

  return (
    <CometRedirectBoundary
      host={process.env.NEXT_PUBLIC_RENTAL_MANAGER_PREFIX_URL}
      onError={onError('unknown error', 500)}
      onUnauthorized={() => onError('unauthorized', 401)}
      {...props}
    />
  );
}

export function withRedirectBoundary<
  Props extends { user: Exclude<ReturnType<typeof useAuth>['user'], null | undefined> },
>(Component: ComponentType<Props>, boundaryProps?: Omit<ComponentProps<typeof RedirectBoundary>, 'children'>) {
  const WithRedirectBoundary: ComponentType<Props> = (props: Props) => (
    <RedirectBoundary {...boundaryProps}>{(user) => <Component {...props} user={user} />}</RedirectBoundary>
  );

  WithRedirectBoundary.displayName = `WithRedirectBoundary(${Component.displayName})`;

  return WithRedirectBoundary;
}
